import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Container } from "../Container/Container";
import { ReactComponent as HomeIcon } from "../../icons/House.svg";
import { ReactComponent as CaretRightIcon } from "../../icons/CaretRight.svg";
import {
  CategoryLinkStyled,
  LocationPanelStyled,
  LocationsStyled,
} from "./LocationPanel.styled";

const SEGMENT_LABELS = {
  shop: "Все товары",
  cart: "Корзину",
  profile: "Профиль",
  orders: "Заказы",
};

const CATEGORY_RU_BY_SLUG = {
  "vse-dlya-geymerov": "Все для геймеров",
  "krasota-i-zdorove": "Красота и здоровье",
  "posuda-dlya-kukhni": "Посуда для кухни",
  "tekhniki-dlya-kukhni": "Техники для кухни",
  "tv-i-audio": "ТВ и аудио",
  "smartfony-i-gadzhety": "Смартфоны и гаджеты",
  "sport-turizm-i-bagazh": "Спорт, туризм и багаж",
  "kholodilniki": "Холодильники",
  "fototekhniki": "Фототехники",
};

const CATALOG_ID_BY_SLUG = {
  "vse-dlya-geymerov": 26,
  "krasota-i-zdorove": 22,
  "posuda-dlya-kukhni": 31,
  "tekhniki-dlya-kukhni": 30,
  "tv-i-audio": 29,
  "smartfony-i-gadzhety": 23,
  "sport-turizm-i-bagazh": 32,
  "kholodilniki": 27,
  "fototekhniki": 21,
};

const API_BASE =
    `${process.env.REACT_APP_API_BASE_URL}/public`;

const humanize = (s = "") =>
    s
        .replace(/-/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^\p{L}/u, (m) => m.toLowerCase());

const cacheByCatalog = new Map();

export const LocationPanel = () => {
  const { pathname, search } = useLocation();
  const segments = useMemo(
      () => pathname.split("/").filter(Boolean),
      [pathname]
  );

  const [overrideCategoryRU, setOverrideCategoryRU] = useState("");
  const [overrideProductRU, setOverrideProductRU] = useState("");
  const abortRef = useRef(null);

  useEffect(() => {
    setOverrideCategoryRU("");
    setOverrideProductRU("");

    if (segments[0] !== "shop" || segments.length < 3) return;

    const params = new URLSearchParams(search);
    const idStr = params.get("id");
    if (!idStr || !/^\d+$/.test(idStr)) return;

    const categorySlug = segments[1];
    const catalogId = CATALOG_ID_BY_SLUG[categorySlug];
    if (!catalogId) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      try {
        let products = cacheByCatalog.get(catalogId);

        if (!products) {
          const url = `${API_BASE}/catalogs/${catalogId}/products?page=0&size=200&sortBy=id&sortDir=asc`;
          const res = await fetch(url, { signal: controller.signal });
          if (!res.ok) return;
          const page = await res.json();
          products = Array.isArray(page?.content) ? page.content : [];
          cacheByCatalog.set(catalogId, products);
        }

        const id = Number(idStr);
        const found = products.find((p) => Number(p?.id) === id);
        if (!found) return;

        const prodRU = String(found.name || found.nameInEnglish || "").trim();
        const catRU = String(
            found.categoryName || found.categoryNameInEnglish || ""
        ).trim();

        if (prodRU) setOverrideProductRU(prodRU);
        if (catRU) setOverrideCategoryRU(catRU);
      } catch {
      }
    })();

    return () => controller.abort();
  }, [segments, search]);

  const crumbs = useMemo(() => {
    const baseCrumbs = [
      { label: "Главная", to: "/" },
      ...segments.map((seg, idx) => {
        const to = "/" + segments.slice(0, idx + 1).join("/");
        if (SEGMENT_LABELS[seg]) return { label: SEGMENT_LABELS[seg], to };
        const decoded = decodeURIComponent(seg.split("?")[0]);
        return { label: humanize(decoded), to };
      }),
    ];

    if (segments[0] === "shop" && segments.length >= 3) {
      const catIndex = baseCrumbs.length - 2;
      const prodIndex = baseCrumbs.length - 1;

      if (overrideCategoryRU) {
        baseCrumbs[catIndex] = {
          ...baseCrumbs[catIndex],
          label: overrideCategoryRU,
        };
      } else {
        const slug = segments[1];
        const labelRU = CATEGORY_RU_BY_SLUG[slug] || baseCrumbs[catIndex].label;
        baseCrumbs[catIndex] = { ...baseCrumbs[catIndex], label: labelRU };
      }

      if (overrideProductRU) {
        baseCrumbs[prodIndex] = {
          ...baseCrumbs[prodIndex],
          label: overrideProductRU,
        };
      }
    }

    return baseCrumbs;
  }, [segments, overrideCategoryRU, overrideProductRU]);

  return (
      <LocationPanelStyled>
        <Container>
          <LocationsStyled>
            {crumbs.map(({ label, to }, i) => (
                <li key={`${to}-${i}`}>
                  {i === 0 ? <HomeIcon /> : <CaretRightIcon />}
                  <CategoryLinkStyled to={to} $current={to === pathname}>
                    {label}
                  </CategoryLinkStyled>
                </li>
            ))}
          </LocationsStyled>
        </Container>
      </LocationPanelStyled>
  );
};
